import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useApi } from "@/lib/api";
import { getLang, setLang, t } from "@/i18n";
import Onboarding from "@/pages/Onboarding";

const Ctx = createContext(null);
export const useSession = () => useContext(Ctx);

export function applyTokens(t) {
  const root = document.documentElement;
  root.dataset.theme = t.theme;
  root.dataset.accent = t.accent;
  root.dataset.font = t.font;
  root.dataset.buttons = t.buttonStyle;
  root.style.setProperty("--font-scale", t.fontScale);
  root.style.setProperty("--spacing-scale", t.spacingScale);
  root.style.setProperty("--radius", `${t.radius}rem`);
}

export function SessionProvider({ children }) {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [ui, setUi] = useState(null);
  const [error, setError] = useState("");

  const setUiState = useCallback((state) => {
    setUi(state);
    applyTokens(state.tokens);
  }, []);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let tries = 0, timer, stop = false;
    const load = () => api.get("/me")
      .then((m) => (m.data.needs_onboarding ? [m, null] : api.get("/ui/prefs").then((u) => [m, u])))
      .then(([m, u]) => {
        if (stop) return;
        if (m.data.org?.id) sessionStorage.setItem("smboi-org", m.data.org.id);
        if (!u) { setMe(m.data); return; }
        // adopt the language picked on the website if the person never chose one in the app
        if (m.data.preferred_language) setLang(m.data.preferred_language);
        else if (getLang() !== "en") api.put("/me/language", { language: getLang() }).catch(() => {});
        setMe({ ...m.data, preferred_language: getLang() });
        setUiState(u.data);
      })
      .catch((e) => {
        if (stop) return;
        // busy server, network blip or a brand-new session: back off and try again before giving up
        const status = e?.response?.status;
        const max = status === 401 || status === 403 ? 3 : 7;
        if (++tries >= max) { setError(t("Could not load your account. Refresh to try again.")); return; }
        const wait = Number(e?.response?.headers?.["retry-after"]) * 1000 || Math.min(1000 * 2 ** (tries - 1), 8000);
        timer = setTimeout(load, wait);
      });
    setError("");
    load();
    return () => { stop = true; clearTimeout(timer); };
  }, [api, setUiState, version]);

  if (error) return (
    <div className="page stack-sm items-start">
      <p data-testid="session-error">{error}</p>
      <button className="btn btn-primary btn-sm" onClick={refresh} data-testid="session-retry-button">{t("Try again")}</button>
    </div>
  );
  if (me?.needs_onboarding) return <Onboarding me={me} onDone={refresh} />;
  if (!me || !ui) return <div className="page faint" data-testid="session-loading">{t("Loading SMBOI…")}</div>;
  return <Ctx.Provider value={{ me, ui, setUiState, refresh }}>{children}</Ctx.Provider>;
}
